import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit, FiCheck, FiX, FiPackage,
  FiDownload, FiCopy, FiAlertCircle, FiCalendar, FiUser,
  FiFileText, FiDollarSign, FiShoppingCart, FiTruck, FiPauseCircle, FiPlayCircle
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { ordenesService, ordenUtils } from '../../services/ordenesService';
import { clientesService } from '../../services';

const VentaDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [orden, setOrden] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadOrdenDetalle();
  }, [id]);

  const loadOrdenDetalle = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await ordenesService.getOrdenById(id);

      if (result.success) {
        console.log('Venta cargada:', result.data);
        setOrden(result.data);

        // Cargar datos del cliente
        if (result.data.client_id) {
          const clienteResult = await clientesService.getCliente(result.data.client_id);
          if (clienteResult.success) {
            setCliente(clienteResult.data);
          }
        }
      } else {
        setError(result.message || 'Venta no encontrada');
      }
    } catch (error) {
      setError('Error de conexión al cargar Orden');
      console.error('Error loading sale:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConvertToOrder = async () => {
    if (!window.confirm(`¿Deseas convertir la cotización ${orden.number} en una orden de compra?`)) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ordenesService.convertToOrder(id);

      if (result.success) {
        await loadOrdenDetalle(); // Recargar datos
        alert('Cotización convertida exitosamente a orden');
      } else {
        setError(result.message || 'Error al convertir la cotización');
      }
    } catch (error) {
      setError('Error al convertir la cotización');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm(`¿Deseas aprobar la Orden ${orden.number}?`)) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ordenesService.approveOrden(id);

      if (result.success) {
        await loadOrdenDetalle(); // Recargar datos
      } else {
        setError(result.message || 'Error al aprobar la Orden');
      }
    } catch (error) {
      setError('Error al aprobar la venta');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    const reason = prompt('Ingresa el motivo del rechazo (opcional):');
    if (reason === null) return;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ordenesService.rejectOrden(id, reason);

      if (result.success) {
        await loadOrdenDetalle();
      } else {
        setError(result.message || 'Error al rechazar la Orden');
      }
    } catch (error) {
      setError('Error al rechazar la venta');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConvertToInvoice = async () => {
    if (!window.confirm(`¿Deseas convertir la venta ${orden.number} en una factura?`)) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ordenesService.convertToInvoice(id);

      if (result.success) {
        // Navegar a la factura creada si existe
        if (result.data?.invoice_id) {
          navigate(`/system/facturacion/${result.data.invoice_id}`);
        } else {
          await loadOrdenDetalle();
        }
      } else {
        setError(result.message || 'Error al convertir la venta a factura');
      }
    } catch (error) {
      setError('Error al convertir la venta a factura');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleComplete = async () => {
    if (!window.confirm(`¿Deseas completar la venta ${orden.number}?`)) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ordenesService.completeOrden(id);

      if (result.success) {
        await loadOrdenDetalle();
      } else {
        setError(result.message || 'Error al completar la venta');
      }
    } catch (error) {
      setError('Error al completar la venta');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    const reason = prompt('Ingresa el motivo de la cancelación:');
    if (!reason || reason.trim() === '') {
      alert('Debes ingresar un motivo para cancelar la orden');
      return;
    }

    if (!window.confirm(`¿Estás seguro de cancelar la orden ${orden.number}? Esta acción no se puede deshacer.`)) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ordenesService.cancelOrden(id, reason);

      if (result.success) {
        await loadOrdenDetalle();
      } else {
        setError(result.message || 'Error al cancelar la orden');
      }
    } catch (error) {
      setError('Error al cancelar la orden');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleHold = async () => {
    const reason = prompt('Ingresa el motivo para detener la orden (opcional):');
    if (reason === null) return;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ordenesService.holdOrden(id, reason);

      if (result.success) {
        await loadOrdenDetalle();
      } else {
        setError(result.message || 'Error al detener la orden');
      }
    } catch (error) {
      setError('Error al detener la orden');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRelease = async () => {
    if (!window.confirm(`¿Deseas liberar la orden ${orden.number}?`)) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ordenesService.releaseOrden(id);

      if (result.success) {
        await loadOrdenDetalle();
      } else {
        setError(result.message || 'Error al liberar la orden');
      }
    } catch (error) {
      setError('Error al liberar la orden');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return <LoadingGrid count={4} message="Cargando venta..." />;
  }

  if (error && !orden) {
    return (
      <div className="error-container">
        <h2>Venta no encontrada</h2>
        <p>{error}</p>
        <button
          className="btn btn--primary"
          onClick={() => navigate('/system/ventas')}
        >
          <FiArrowLeft /> Volver a Ventas
        </button>
      </div>
    );
  }

  const statusInfo = {
    label: ordenUtils.getStatusLabel(orden.status),
    color: ordenUtils.getStatusColor(orden.status)
  };

  return (
    <div className="cliente-form-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/ventas')}
        >
          <FiArrowLeft /> Volver a Ventas
        </button>

        <h1 className="form-title">
          {orden.number || `VENTA #${id}`}
        </h1>

        <div className="form-actions">
          <button className="btn btn--outline">
            <FiDownload /> Descargar PDF
          </button>

          {ordenUtils.canConvertToInvoice(orden) && (
            <button
              className="btn btn--primary"
              onClick={handleConvertToInvoice}
              disabled={isProcessing}
            >
              <FiFileText /> Convertir a Factura
            </button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => setError(null)}
          >
            Cerrar
          </button>
        </div>
      )}

      {/* Status Banner */}
      <div className={`status-banner status-banner--${statusInfo.color}`} style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FiShoppingCart size={24} />
          <div>
            <div style={{ fontWeight: '600', fontSize: '1.1em' }}>{statusInfo.label}</div>
            <div style={{ fontSize: '0.9em', opacity: 0.9 }}>
              {orden.source === 'PORTAL' ? 'Venta desde Portal del Cliente' : 'Venta desde Admin'}
            </div>
          </div>
        </div>
        <span className={`badge badge--${statusInfo.color}`} style={{ fontSize: '0.95em', padding: '6px 12px' }}>
          {orden.order_type === 'ESTIMATE' ? 'Estimado' : 'Orden'}
        </span>
      </div>

      {/* Contenido en formato de formulario (solo lectura) */}
      <div className="cliente-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Información del Cliente */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Información del Cliente</h2>
              </div>

              {cliente ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Cliente</label>
                    <div className="form-value" style={{ fontWeight: '600', fontSize: '1.05em' }}>
                      {cliente.company_name || cliente.full_name}
                    </div>
                  </div>

                  {cliente.tax_id && (
                    <div className="form-group">
                      <label className="form-label">NIT/Cédula</label>
                      <div className="form-value">{cliente.tax_id}</div>
                    </div>
                  )}

                  {cliente.email && (
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <div className="form-value">{cliente.email}</div>
                    </div>
                  )}

                  {cliente.phone && (
                    <div className="form-group">
                      <label className="form-label">Teléfono</label>
                      <div className="form-value">{cliente.phone}</div>
                    </div>
                  )}
                </>
              ) : (
                <div className="form-group">
                  <label className="form-label">ID del Cliente</label>
                  <div className="form-value" style={{ fontWeight: '600', fontSize: '1.05em' }}>
                    Cliente #{orden.client_id || 'N/A'}
                  </div>
                </div>
              )}
            </div>

            {/* Información de la Venta */}
            <div className="form-section">
              <div className="section-header">
                <FiFileText className="section-icon" />
                <h3 className="section-title">Información de la Venta</h3>
              </div>

              {orden.title && (
                <div className="form-group">
                  <label className="form-label">Título</label>
                  <div className="form-value">{orden.title}</div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Número de Venta</label>
                <div className="form-value" style={{ fontWeight: '600' }}>{orden.number}</div>
              </div>

              <div className="form-group">
                <label className="form-label">ID de Venta</label>
                <div className="form-value">#{orden.id}</div>
              </div>

              {orden.source && (
                <div className="form-group">
                  <label className="form-label">Origen</label>
                  <div className="form-value">
                    <span className={`badge badge--${orden.source === 'PORTAL' ? 'info' : 'secondary'}`}>
                      {orden.source === 'PORTAL' ? 'Portal del Cliente' : 'Administrador'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Fechas */}
            <div className="form-section">
              <div className="section-header">
                <FiCalendar className="section-icon" />
                <h3 className="section-title">Fechas</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Fecha de Creación</label>
                <div className="form-value">{formatDate(orden.created_at)}</div>
              </div>

              {orden.updated_at && (
                <div className="form-group">
                  <label className="form-label">Última Actualización</label>
                  <div className="form-value">{formatDate(orden.updated_at)}</div>
                </div>
              )}

              {orden.expected_delivery_date && (
                <div className="form-group">
                  <label className="form-label">
                    <FiTruck size={14} style={{ marginRight: '6px' }} />
                    Fecha de Entrega Esperada
                  </label>
                  <div className="form-value">{formatDate(orden.expected_delivery_date)}</div>
                </div>
              )}
            </div>

            {/* Resumen Financiero */}
            <div className="form-section">
              <div className="section-header">
                <FiDollarSign className="section-icon" />
                <h3 className="section-title">Resumen Financiero</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Subtotal</label>
                <div className="form-value">{formatCurrency(orden.subtotal)}</div>
              </div>

              {orden.tax_amount > 0 && (
                <div className="form-group">
                  <label className="form-label">Impuestos</label>
                  <div className="form-value">{formatCurrency(orden.tax_amount)}</div>
                </div>
              )}

              {orden.discount_amount > 0 && (
                <div className="form-group">
                  <label className="form-label">Descuento</label>
                  <div className="form-value" style={{ color: '#10b981' }}>
                    -{formatCurrency(orden.discount_amount)}
                  </div>
                </div>
              )}

              <div className="form-group" style={{ borderTop: '2px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' }}>
                <label className="form-label" style={{ fontSize: '1.1em', fontWeight: '600' }}>Total</label>
                <div className="form-value" style={{ fontSize: '1.3em', fontWeight: '700', color: '#1f2937' }}>
                  {formatCurrency(orden.total)}
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Productos */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h2 className="section-title">
                  Productos ({orden.items?.length || 0} items)
                </h2>
              </div>

              {orden.items && orden.items.length > 0 ? (
                <div style={{ marginTop: '16px' }}>
                  {orden.items.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        marginBottom: '10px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {item.description || item.product_name || `Producto #${item.product_id}`}
                          </div>
                          {item.product_id && (
                            <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                              ID Producto: {item.product_id}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: '600' }}>
                            {formatCurrency(item.subtotal || item.total)}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em', color: '#6b7280' }}>
                        <span>Cantidad: {parseFloat(item.quantity)}</span>
                        <span>Precio unit: {formatCurrency(item.unit_price)}</span>
                      </div>
                      {item.notes && (
                        <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#6b7280', fontStyle: 'italic' }}>
                          Nota: {item.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{ marginTop: '16px' }}>
                  <p>No hay productos en esta venta</p>
                </div>
              )}
            </div>

            {/* Información Adicional */}
            {(orden.notes || orden.delivery_address) && (
              <div className="form-section">
                <div className="section-header">
                  <FiFileText className="section-icon" />
                  <h3 className="section-title">Información Adicional</h3>
                </div>

                {orden.notes && (
                  <div className="form-group">
                    <label className="form-label">Notas</label>
                    <div className="form-value" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#fef3c7', padding: '12px', borderRadius: '6px', fontSize: '0.95em' }}>
                      {orden.notes}
                    </div>
                  </div>
                )}

                {orden.delivery_address && (
                  <div className="form-group">
                    <label className="form-label">Dirección de Entrega</label>
                    <div className="form-value" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '6px', fontSize: '0.95em' }}>
                      {orden.delivery_address}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Acciones Disponibles */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Acciones Disponibles</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
                {/* 1. CONVERTIR COTIZACIÓN A ORDEN - Solo para ESTIMATE */}
                {(orden.order_type === 'ESTIMATE' || orden.type === 'ESTIMATE') && orden.status !== 'CONVERTED' && (
                  <button
                    className="btn btn--success"
                    onClick={handleConvertToOrder}
                    disabled={isProcessing}
                    style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                  >
                    <FiShoppingCart size={18} style={{ marginRight: '8px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: '600' }}>Convertir a Orden</div>
                      <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                        Convertir esta cotización en una orden de compra
                      </div>
                    </div>
                  </button>
                )}

                {/* 2. APROBAR - Solo si es del PORTAL y está pendiente */}
                {orden.source === 'PORTAL' && orden.status === 'PENDING_APPROVAL' && (
                  <button
                    className="btn btn--success"
                    onClick={handleApprove}
                    disabled={isProcessing}
                    style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                  >
                    <FiCheck size={18} style={{ marginRight: '8px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: '600' }}>Aprobar</div>
                      <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                        Aprobar esta orden del portal
                      </div>
                    </div>
                  </button>
                )}

                {/* 2. LIBERAR - Solo si está aprobada (después de aprobar) */}
                {orden.status === 'APPROVED' && (
                  <button
                    className="btn btn--info"
                    onClick={handleRelease}
                    disabled={isProcessing}
                    style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                  >
                    <FiPlayCircle size={18} style={{ marginRight: '8px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: '600' }}>Liberar</div>
                      <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                        Liberar la orden para facturación
                      </div>
                    </div>
                  </button>
                )}

                {/* 3. CONVERTIR A FACTURA - Solo si está liberada (después de liberar) */}
                {orden.status === 'RELEASED' && (
                  <button
                    className="btn btn--primary"
                    onClick={handleConvertToInvoice}
                    disabled={isProcessing}
                    style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                  >
                    <FiFileText size={18} style={{ marginRight: '8px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: '600' }}>Convertir a Factura</div>
                      <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                        Generar factura desde esta orden
                      </div>
                    </div>
                  </button>
                )}

                {/* 4. DETENER - Disponible en varios estados excepto convertida/cancelada */}
                {(orden.status === 'PENDING_APPROVAL' || orden.status === 'APPROVED' || orden.status === 'RELEASED') && (
                  <button
                    className="btn btn--warning"
                    onClick={handleHold}
                    disabled={isProcessing}
                    style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                  >
                    <FiPauseCircle size={18} style={{ marginRight: '8px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: '600' }}>Detener</div>
                      <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                        Poner la orden en espera
                      </div>
                    </div>
                  </button>
                )}

                {/* 5. CANCELAR - Solo en estados iniciales (sin flujo previo) */}
                {(orden.status === 'PENDING_APPROVAL' || orden.status === 'DRAFT' || orden.status === 'ON_HOLD') && (
                  <button
                    className="btn btn--danger"
                    onClick={handleCancel}
                    disabled={isProcessing}
                    style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                  >
                    <FiX size={18} style={{ marginRight: '8px' }} />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: '600' }}>Cancelar Orden</div>
                      <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                        Cancelar esta orden (no se puede deshacer)
                      </div>
                    </div>
                  </button>
                )}

                {/* Mensaje informativo si no hay acciones disponibles */}
                {orden.status === 'CONVERTED' && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#d1fae5',
                    borderRadius: '6px',
                    color: '#065f46',
                    fontSize: '0.9em'
                  }}>
                    <FiCheck style={{ marginRight: '8px' }} />
                    Esta orden ya fue convertida a factura. No hay acciones disponibles.
                  </div>
                )}

                {orden.status === 'CLOSED_WON' && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#d1fae5',
                    borderRadius: '6px',
                    color: '#065f46',
                    fontSize: '0.9em'
                  }}>
                    <FiCheck style={{ marginRight: '8px' }} />
                    Esta orden está completada. No hay acciones disponibles.
                  </div>
                )}

                {orden.status === 'CLOSED_LOST' && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    color: '#991b1b',
                    fontSize: '0.9em'
                  }}>
                    <FiX style={{ marginRight: '8px' }} />
                    Esta orden fue cancelada. No hay acciones disponibles.
                  </div>
                )}

                {orden.status === 'REJECTED' && (
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    color: '#991b1b',
                    fontSize: '0.9em'
                  }}>
                    <FiX style={{ marginRight: '8px' }} />
                    Esta orden fue rechazada. No hay acciones disponibles.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VentaDetalle;
